import { useEffect, useRef, useState } from "react";
import "./ThickSlider.css";

export default function ThickSlider({
  label,
  value,
  onChange,
  min,
  max,
  unit = "",
  step = 1,
  inputDebounceMs = 250
}) {
  const trackRef = useRef(null);
  const [showBubble, setShowBubble] = useState(false);
  const [trackWidth, setTrackWidth] = useState(0);

  const [textValue, setTextValue] = useState(String(value));
  const debounceTimerRef = useRef(null);

  const clamp = (n) => Math.min(max, Math.max(min, n));
  const inRange = (n) => n >= min && n <= max;

  useEffect(() => {
    setTextValue(String(value));
  }, [value]);

  const fraction = (value - min) / (max - min);

  useEffect(() => {
    if (!trackRef.current) return;

    const el = trackRef.current;
    const update = () => setTrackWidth(el.getBoundingClientRect().width);

    update();
    const ro = new ResizeObserver(() => update());
    ro.observe(el);

    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    setShowBubble(true);
    const t = setTimeout(() => setShowBubble(false), 300);
    return () => clearTimeout(t);
  }, [value]);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, []);

  const bubbleLeft = trackWidth * fraction;

  const commitTextToValueDebounced = (nextText) => {
    setTextValue(nextText);

    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);

    debounceTimerRef.current = setTimeout(() => {
      const trimmed = nextText.trim();

      if (
        trimmed === "" ||
        trimmed === "-" ||
        trimmed === "." ||
        trimmed === "-."
      ) {
        return;
      }

      const parsed = Number(trimmed);
      if (!Number.isFinite(parsed)) return;

      if (!inRange(parsed)) return;

      const snapped = Math.round(parsed / step) * step;

      if (!inRange(snapped)) return;

      onChange(snapped);
    }, inputDebounceMs);
  };

  const commitNow = () => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);

    const trimmed = textValue.trim();

    if (
      trimmed === "" ||
      trimmed === "-" ||
      trimmed === "." ||
      trimmed === "-."
    ) {
      setTextValue(String(value));
      return;
    }

    const parsed = Number(trimmed);
    if (!Number.isFinite(parsed)) {
      setTextValue(String(value));
      return;
    }

    const clamped = clamp(parsed);
    const snapped = Math.round(clamped / step) * step;
    const next = clamp(snapped);

    onChange(next);
    setTextValue(String(next));
  };

  return (
    <div className="slider-wrapper">
      <div className="slider-label">{label}</div>

      <div className="slider-control-row">
        <div className="slider-track-col">
          <div className="slider-track-wrapper" ref={trackRef}>
            <div className="slider-track" />
            <div
              className="slider-track-filled"
              style={{ width: `${fraction * 100}%` }}
            />

            {showBubble && (
              <div className="slider-bubble" style={{ left: bubbleLeft }}>
                {Math.round(value)}
                {unit}
              </div>
            )}

            <input
              type="range"
              className="slider-input"
              min={min}
              max={max}
              step={step}
              value={value}
              onChange={(e) => onChange(Number(e.target.value))}
            />
          </div>
        </div>

        <div className="slider-number">
          <input
            className="slider-number-input"
            type="number"
            inputMode="numeric"
            min={min}
            max={max}
            step={step}
            value={textValue}
            onChange={(e) => commitTextToValueDebounced(e.target.value)}
            onBlur={commitNow}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                commitNow();
              }
            }}
          />
          {unit && <span className="slider-number-unit">{unit}</span>}
        </div>
      </div>

      <div className="slider-range">
        <span>{min}</span>
        <span>{max}</span>
      </div>
    </div>
  );
}
